import type { Event } from '@/types'

export interface EventDownload {
  url: string
  filename: string
}

/**
 * Resolve the downloadable variants for an event. Mirrors the variant logic the
 * event detail modal has always used:
 *   - original:  the raw camera file (media_details.file_url)
 *   - annotated: video -> processed MP4 (detection_video_url);
 *                image -> annotated JPEG snapshot (thumbnail_url)
 * A variant is null when its URL is absent (no media, failed video processing, etc.).
 */
/** Best-effort file extension from a URL path (ignores the query string). Falls back when absent. */
function extFromUrl(url: string, fallback: string): string {
  try {
    const seg = new URL(url).pathname.split('/').pop() ?? ''
    const dot = seg.lastIndexOf('.')
    if (dot > 0 && dot < seg.length - 1) {
      const ext = seg.slice(dot + 1).toLowerCase()
      if (/^[a-z0-9]{1,5}$/.test(ext)) return ext
    }
  } catch {
    // malformed URL — use the fallback
  }
  return fallback
}

export function getEventDownloads(
  event: Event,
  fullSizeUrl: string | null = event.media_details?.full_size_url ?? null,
): {
  original: EventDownload | null
  annotated: EventDownload | null
} {
  const isVideo = event.media_details?.media_type === 'video'
  const originalUrl = event.media_details?.file_url ?? null
  // Original keeps the camera's real extension (originals may be .avi/.mov/.mkv/etc.).
  const originalExt = originalUrl ? extFromUrl(originalUrl, isVideo ? 'mp4' : 'jpg') : (isVideo ? 'mp4' : 'jpg')

  // Annotated = the quick-download target. For a VIDEO event this must resolve to a video,
  // never the thumbnail: prefer the processed (bounding-box) MP4, else fall back to the
  // original video. For an IMAGE event it's the annotated JPEG snapshot.
  let annotatedUrl: string | null
  let annotatedExt: string
  if (isVideo) {
    if (event.detection_video_url) {
      annotatedUrl = event.detection_video_url
      annotatedExt = 'mp4'
    } else {
      annotatedUrl = originalUrl
      annotatedExt = originalExt
    }
  } else {
    annotatedUrl = event.thumbnail_url
    annotatedExt = 'jpg'
  }

  return {
    original: fullSizeUrl
      ? { url: fullSizeUrl, filename: `detercam-event-${event.id}-original.jpg` }
      : originalUrl
      ? { url: originalUrl, filename: `detercam-event-${event.id}-original.${originalExt}` }
      : null,
    annotated: annotatedUrl
      ? { url: annotatedUrl, filename: `detercam-event-${event.id}-annotated.${annotatedExt}` }
      : null,
  }
}

/**
 * Download a (cross-origin S3 presigned) URL as a file. fetch -> blob -> anchor.
 * Falls back to opening in a new tab if the fetch fails (e.g. expired URL / CORS).
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  try {
    // cache: 'no-store' forces a fresh CORS request. Without it the browser may reuse the
    // no-CORS (opaque) response the card's <img>/<video> already cached for this same URL —
    // that response lacks Access-Control-Allow-Origin, so the fetch throws and we fall back
    // to opening a new tab (seen as: images open in a new tab while videos download fine).
    const res = await fetch(url, { cache: 'no-store' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(objectUrl)
  } catch {
    // Fallback: open in new tab if fetch fails
    window.open(url, '_blank')
  }
}
